import { computed, type Ref } from "vue";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import {
    isFinishedRunStatus,
    type AttemptPage,
    type ContentPreview,
    type HttpAttempt,
    type RunHistory,
    type RunWorkflowVersion,
    type TransactionDetail,
    type TransactionPage,
} from "@/types";
import type { Paged } from "@/queries/projects";
import {
    serverFilterParamsToQueryString,
    type ServerFilterParams,
} from "@/views/builder/composables/useHistoryFilters";

const BASE = `${import.meta.env.VITE_API_URL}/runs`;

/** One product the org-wide Run History can be filtered by. Mirrors the backend RunFilterProductDto. */
export interface RunFilterProduct {
    id: string;
    name: string;
}

// READ — the products the org's run history can be narrowed by (owned + subscribed, attached to a
// project). Backs the Run History page's product-filter dropdown; the set matches what the product
// filter actually resolves against, so every option can return runs.
export function useOrgRunFilterProducts() {
    const { data, isFetching: runFilterProductsLoading, error: runFilterProductsError } = useQuery({
        queryKey: ["run-filter-products"],
        queryFn: () => fetchWrapper.get<RunFilterProduct[]>(`${BASE}/products`),
    });
    const runFilterProducts = computed(() => data.value ?? []);
    return { runFilterProducts, runFilterProductsLoading, runFilterProductsError: runFilterProductsError as Ref<ApiError | null> };
}

// READ — one offset page of every run in the org, across all projects (the org-wide Run History page).
// Runs are partitioned by org, so this is the same cost as a project's page without the project bound.
// Its own query-key namespace (`all-runs`), separate from `project-runs`, so live-progress cache patches
// for one surface never touch the other. filterParams drives server-side filtering/sorting/paging exactly
// as the per-project hook does — the serialised query string is part of the key, so a change refetches.
// keepPreviousData holds the current page while the next loads, so paging doesn't blank the list.
export function useAllRuns(filterParams?: Ref<ServerFilterParams>) {
    const queryString = computed(() =>
        filterParams ? serverFilterParamsToQueryString(filterParams.value) : ""
    );
    const { data, isLoading: allRunsLoading, isFetching: allRunsFetching, error: allRunsError } = useQuery({
        queryKey: computed(() => ["all-runs", queryString.value]),
        queryFn: () => {
            const qs = queryString.value ? `?${queryString.value}` : "";
            return fetchWrapper.get<Paged<RunHistory>>(`${BASE}${qs}`);
        },
        placeholderData: keepPreviousData,
    });
    const allRuns = computed(() => data.value?.items ?? []);
    const allRunsTotal = computed(() => data.value?.totalCount ?? 0);

    return {
        allRuns,
        allRunsTotal,
        allRunsLoading,
        allRunsFetching,
        allRunsError: allRunsError as Ref<ApiError | null>,
    };
}

/** One bounded page of direct children, independent of the search result's filters. */
export function useChildRuns(runId: Ref<string>, page: Ref<number>, enabled: Ref<boolean>, parentIsLive: Ref<boolean>) {
    const pageSize = 20;
    const query = useQuery<Paged<RunHistory>, ApiError>({
        queryKey: computed(() => ["child-runs", runId.value, page.value]),
        enabled,
        queryFn: () => fetchWrapper.get<Paged<RunHistory>>(
            `${BASE}/${encodeURIComponent(runId.value)}/children?skip=${page.value * pageSize}&take=${pageSize}`
        ),
        // Expanded children can start or finish without belonging to the current search page.
        refetchInterval: (query) => enabled.value && (parentIsLive.value ||
            query.state.data?.items.some(run => !isFinishedRunStatus(run.status))) ? 10000 : false,
    });
    return {
        childRuns: computed(() => query.data.value?.items ?? []),
        childRunsTotal: computed(() => query.data.value?.totalCount ?? 0),
        childRunsLoading: query.isFetching,
        childRunsError: query.error,
        retryChildRuns: query.refetch,
        pageSize,
    };
}

// READ — single run, called explicitly after startWorkflow returns a runId
export function useGetWorkflowRun() {
    const queryClient = useQueryClient();
    return (runId: string) =>
        queryClient.fetchQuery<RunHistory>({
            queryKey: ["workflow-run", runId],
            queryFn: () => fetchWrapper.get<RunHistory>(`${BASE}/${runId}`),
        });
}

// READ — one page of a service execution's transactions (#6009). Lightweight metadata only; bodies are
// fetched on demand via useTransactionBody.
//
// Keyset, not offset: a page starts after the last id of the page before it. The engine's store has no
// index over a transaction's position, so an offset would make it read every row it steps over — which is
// what made expanding a large service slow. `status` narrows to one outcome and costs the whole service,
// so it is a filter the user asks for rather than one a page load pays.
export function useServiceTransactions(
    runId: Ref<string>,
    serviceId: Ref<string>,
    after: Ref<string>,
    take: Ref<number>,
    status: Ref<string | null>,
) {
    const {data, isFetching, error} = useQuery({
        queryKey: computed(() => [
            "service-transactions", runId.value, serviceId.value,
            after.value, take.value, status.value,
        ]),
        queryFn: () => {
            const query = new URLSearchParams({after: after.value, take: String(take.value)});
            if (status.value) query.set("status", status.value);
            return fetchWrapper.get<TransactionPage>(
                `${BASE}/${runId.value}/services/${serviceId.value}/transactions?${query}`,
            );
        },
        enabled: computed(() => !!runId.value && !!serviceId.value),
        placeholderData: keepPreviousData,
    });
    const transactions = computed(() => data.value?.items ?? []);
    const transactionsNext = computed(() => data.value?.next ?? null);
    return {
        transactions,
        transactionsNext,
        transactionsFetching: isFetching,
        transactionsError: error as Ref<ApiError | null>,
    };
}

// READ — one transaction's request/response metadata + headers (no body). Enabled when an id is set.
export function useTransactionDetail(
    runId: Ref<string>,
    serviceId: Ref<string>,
    transactionId: Ref<string | null>,
) {
    const {data, isFetching, error} = useQuery({
        queryKey: computed(() => [
            "transaction-detail", runId.value, serviceId.value, transactionId.value,
        ]),
        queryFn: () =>
            fetchWrapper.get<TransactionDetail>(
                `${BASE}/${runId.value}/services/${serviceId.value}/transactions/${transactionId.value}`,
            ),
        enabled: computed(() => !!transactionId.value),
    });
    return {
        transactionDetail: data,
        transactionDetailFetching: isFetching,
        transactionDetailError: error as Ref<ApiError | null>,
    };
}

// READ — the workflow version a run pinned, which is what ran rather than what the builder holds now. A pinned
// version never changes, so one read serves the page.
export function useRunWorkflowVersion(runId: Ref<string>) {
    const {data, error} = useQuery({
        queryKey: computed(() => ["run-workflow-version", runId.value]),
        queryFn: () =>
            fetchWrapper.get<RunWorkflowVersion>(`${BASE}/${runId.value}/workflow-version`),
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
    });
    return {runWorkflowVersion: data, runWorkflowVersionError: error as Ref<ApiError | null>};
}

const ATTEMPT_PAGE_SIZE = 10;

// READ — one page of a transaction's attempts, oldest first. `after` is the `next` of the page before, empty for
// the first page. The page on screen stays while the next one loads.
export function useTransactionAttempts(
    runId: Ref<string>,
    serviceId: Ref<string>,
    transactionId: Ref<string>,
    after: Ref<string>,
) {
    const {data, isFetching, error, refetch} = useQuery({
        queryKey: computed(() => [
            "transaction-attempts", runId.value, serviceId.value, transactionId.value, after.value,
        ]),
        queryFn: () => {
            const query = new URLSearchParams({after: after.value, take: String(ATTEMPT_PAGE_SIZE)});
            return fetchWrapper.get<AttemptPage>(
                `${BASE}/${runId.value}/services/${serviceId.value}/transactions/${transactionId.value}/attempts?${query}`,
            );
        },
        placeholderData: keepPreviousData,
    });
    return {
        attemptPage: data,
        attemptsFetching: isFetching,
        attemptsError: error as Ref<ApiError | null>,
        refetchAttempts: refetch,
    };
}

// READ — one attempt of an HTTP transaction, named by the id its row in the attempts page carries. The attempts
// table reads it when a row opens, so an attempt's request and response are read only for the row shown.
export function useTransactionAttempt(
    runId: Ref<string>,
    serviceId: Ref<string>,
    transactionId: Ref<string | null>,
    attemptId: Ref<string | null>,
) {
    const {data, isFetching, error, refetch} = useQuery({
        queryKey: computed(() => [
            "transaction-attempt", runId.value, serviceId.value, transactionId.value,
            attemptId.value,
        ]),
        queryFn: () =>
            fetchWrapper.get<HttpAttempt>(
                `${BASE}/${runId.value}/services/${serviceId.value}/transactions/${transactionId.value}/attempts/${attemptId.value}`,
            ),
        enabled: computed(() => !!transactionId.value && attemptId.value !== null),
        placeholderData: keepPreviousData,
    });
    return {
        attempt: data,
        attemptFetching: isFetching,
        attemptError: error as Ref<ApiError | null>,
        refetchAttempt: refetch,
    };
}

/** One side of one attempt. */
export type AttemptSide = { attemptId: string; kind: "request" | "response" };

/**
 * Which body to read: the transaction's result; output `i` as a child received it; element `i` of segmented
 * content; or one side of one attempt.
 */
export type BodySource = "result" | { output: number } | { element: number } | AttemptSide;

// The URL a download link points at — an authenticated GET like every other call here, reached by href rather
// than fetch. The session rides an HttpOnly SameSite=None cookie, so the browser attaches it and streams the
// file straight to disk; pulling a 40 MB export through fetch would hold all of it in memory first, which is
// the case this endpoint exists for. Open it in a new tab: a link cannot send the X-Requested-With header that
// makes an expired session answer 401 rather than redirect, so a stale one lands in a throwaway tab instead of
// navigating the explorer away.
export function transactionBodyDownloadUrl(
    runId: string,
    serviceId: string,
    transactionId: string,
    source: BodySource,
): string {
    const base = `${BASE}/${runId}/services/${serviceId}/transactions/${transactionId}`;
    // An output or an element downloads as the transaction's whole result.
    if (source === "result" || "output" in source || "element" in source) return `${base}/body/download`;
    return `${base}/attempts/${source.attemptId}/${source.kind}-body/download`;
}

function bodyPath(source: BodySource): string {
    if (source === "result") return "/body";
    if ("output" in source) return `/body?output=${source.output}`;
    if ("element" in source) return `/body?element=${source.element}`;
    return `/attempts/${source.attemptId}/${source.kind}-body`;
}

// READ — on-demand body fetch. Returns a fetcher so the UI reads a (possibly large) body only when the
// user opens it. The source says which body; naming the attempt is what makes every attempt readable.
export function useTransactionBody() {
    const queryClient = useQueryClient();
    return (
            runId: string,
        serviceId: string,
        transactionId: string,
        source: BodySource,
    ) => {
        const url = `${BASE}/${runId}/services/${serviceId}/transactions/${transactionId}${bodyPath(source)}`;
        return queryClient.fetchQuery<ContentPreview>({
            queryKey: ["transaction-body", url],
            queryFn: () => fetchWrapper.get<ContentPreview>(url),
        });
    };
}
